import { cn } from "@/shared/lib/utils";
import { tanStackFeatures } from "./features";
import { TanStackColumn } from "./types";
import type {
  AppColumn,
  AppColumnDef,
  AppHeader,
  AppTable,
} from "./features";
import type {
  ColumnPinningState,
  ColumnOrderState,
  RowPinningState,
  SortingState,
  ColumnFiltersState,
  ColumnVisibilityState,
  ColumnSizingState,
  RowData,
} from "@tanstack/react-table";

// Helper function to get resize handle style with performance optimization
export const getResizeHandleProps = <T extends RowData>(header: AppHeader<T>, isLastColumn?: boolean) => ({
  onDoubleClick: () => header.column.resetSize(),
  onMouseDown: header.getResizeHandler(),
  onTouchStart: header.getResizeHandler(),
  className: cn(
    "absolute right-0 top-0 h-full w-1.5 cursor-col-resize select-none touch-none transition-all duration-200",
    isLastColumn
      ? "border-r border-transparent group-hover:border-gray-200"
      : "border-r border-gray-200",
    "hover:bg-blue-500 hover:border-blue-500 active:bg-blue-600 active:border-blue-600",
    header.column.getIsResizing() && "bg-blue-500 border-blue-500",
  ),
});

// Merges consumer-supplied row attributes (getRowProps) with the table's own row
// behaviour. The consumer onClick runs first so it can stopPropagation before the
// table's onRowClick fires; className stays out so each renderer composes its own.
export const mergeRowProps = <T extends RowData>(
  getRowProps: ((row: T) => React.HTMLAttributes<HTMLTableRowElement>) | undefined,
  rowOriginal: T,
  onRowClick?: (row: T) => void,
) => {
  const { className: rowClassName, onClick: rowOnClick, ...restRowProps } = getRowProps?.(rowOriginal) ?? {};
  return {
    rowClassName,
    restRowProps,
    onClick: (e: React.MouseEvent<HTMLTableRowElement>) => {
      rowOnClick?.(e);
      if (!e.isPropagationStopped()) {
        onRowClick?.(rowOriginal);
      }
    },
  };
};

// Helper function to generate pinning styles (clean sticky positioning)
export const getCommonPinningStyles = <T extends RowData>(
  column: AppColumn<T>,
  context: "header" | "cell" = "cell",
): React.CSSProperties => {
  const isPinned = column.getIsPinned();

  return {
    left: isPinned === "start" ? `${column.getStart("start")}px` : undefined,
    right: isPinned === "end" ? "0px" : undefined, // Always stick to right edge
    position: isPinned ? "sticky" : "relative",
    width: column.getSize(),
    zIndex: isPinned ? 10 : 0, // Higher z-index for better layering
    backgroundColor: isPinned ? (context === "header" ? "#f9fafb" : "#ffffff") : undefined,
  };
};

// Helper functions for DraggableHeader
export const getHeaderStyle = <T extends RowData>(
  header: AppHeader<T>,
  enableColumnPinning: boolean,
  style: React.CSSProperties,
) => ({
  width: `calc(var(--header-${header.id}-size) * 1px)`,
  ...(enableColumnPinning
    ? getCommonPinningStyles(header.column, "header")
    : { position: "relative" as const }),
  ...style,
});

export const getHeaderClassName = <T extends RowData>(
  column: TanStackColumn<T>,
  enableSorting: boolean,
  header: AppHeader<T>,
  isDragging: boolean,
) =>
  cn(
    "group relative px-6 py-3 !text-left text-xs font-semibold uppercase tracking-wider min-h-[3.5rem] align-middle",
    column.isSelectionColumn && "!px-2 !py-2",
    column.columnClassName,
    enableSorting && header.column.getCanSort() && "cursor-pointer select-none",
    isDragging && "z-50",
    // Default text color - sorting color is handled by SortableHeader component
    "text-gray-600",
  );

// Helper function to get header text content
export const getHeaderText = <T extends RowData>(column: TanStackColumn<T>): string => {
  // Handle string headers
  if (typeof column.header === "string") {
    return column.header;
  }

  // Handle function headers (common in TanStack Table)
  if (typeof column.header === "function") {
    try {
      // Try to extract text from React elements if possible
      const headerContent = column.header as unknown;
      if (headerContent && typeof headerContent === "string") {
        return headerContent;
      }
    }
    catch {
      // Fallback to other options
    }
  }

  // Fallback options
  if (column.label) {
    return column.label;
  }

  if (column.id) {
    // Convert camelCase or snake_case to readable text
    return column.id
      .replace(/([A-Z])/g, " $1")
      .replace(/_/g, " ")
      .trim()
      .toUpperCase();
  }

  return "";
};

// Helper function to calculate optimal column width based on header content
export const calculateOptimalWidth = <T extends RowData>(column: TanStackColumn<T>): number => {
  if (column.isSelectionColumn) return 60;

  // Use more conservative defaults instead of aggressive text-based calculations
  const defaultWidth = 150;
  const minWidth = column.minSize ?? 120;
  const maxWidth = column.maxSize ?? (column.canWidthGrow ? 1000 : 300);

  // If a specific size is provided, use it
  if (column.size) {
    return Math.max(minWidth, Math.min(maxWidth, column.size));
  }

  return Math.max(minWidth, Math.min(maxWidth, defaultWidth));
};

// Helper functions to reduce complexity
export const processTableColumns = <T extends RowData>(
  columns: TanStackColumn<T>[],
  enableColumnResizing: boolean,
  enableColumnPinning: boolean,
  enableColumnFiltering: boolean,
  enableColumnOrdering: boolean,
) => {
  return columns.map((col, index) => {
    const optimalWidth = calculateOptimalWidth(col);
    const accessorKey = (col as { accessorKey?: string }).accessorKey;
    const resolvedId = col.id || accessorKey || `column-${index}`;

    return {
      ...col,
      enableResizing: col.enableResizing ?? enableColumnResizing,
      enablePinning: col.enablePinning ?? enableColumnPinning,
      enableColumnFilter: col.enableColumnFilter ?? enableColumnFiltering,
      enableOrdering: col.enableOrdering ?? enableColumnOrdering,
      size: col.size ?? optimalWidth,
      minSize: col.minSize ?? (col.isSelectionColumn ? 60 : 120),
      maxSize: col.maxSize ?? (col.canWidthGrow ? 1000 : 400),
      id: resolvedId,
      header: col.header || col.label || `Column ${index + 1}`,
    };
  });
};

// Helper functions to reduce complexity
export const createTableConfig = <T extends RowData>(
  data: T[],
  processedColumns: AppColumnDef<T>[],
  state: Record<string, unknown>,
  handlers: Record<string, unknown>,
  enableColumnResizing: boolean,
  enableSorting: boolean,
  enableColumnPinning: boolean,
  enableColumnOrdering: boolean,
  enableRowPinning: boolean,
  maxMultiSortColCount?: number,
  enableSortingRemoval?: boolean,
  isMultiSortEvent?: () => boolean,
  manualSorting?: boolean,
) => {
  const config = {
    data,
    columns: processedColumns,
    state,
    ...handlers,
    features: tanStackFeatures,
    enableColumnResizing,
    enableSorting,
    manualSorting: manualSorting ?? false,
    maxMultiSortColCount,
    enableSortingRemoval,
    isMultiSortEvent,
    columnResizeMode: "onChange" as const,
  };

  if (enableColumnPinning) {
    (config as Record<string, unknown>).enableColumnPinning =
      enableColumnPinning;
  }
  if (enableColumnOrdering) {
    (config as Record<string, unknown>).enableColumnOrdering =
      enableColumnOrdering;
  }
  if (enableRowPinning) {
    (config as Record<string, unknown>).enableRowPinning = enableRowPinning;
  }

  return config;
};

export const createTableState = (
  sorting: SortingState,
  columnFilters: ColumnFiltersState,
  columnVisibility: ColumnVisibilityState,
  columnSizing: ColumnSizingState,
  enableColumnPinning: boolean,
  columnPinning: ColumnPinningState,
  enableColumnOrdering: boolean,
  columnOrder: ColumnOrderState,
  enableRowPinning: boolean,
  rowPinning: RowPinningState,
) => {
  const state = {
    sorting,
    columnFilters,
    columnVisibility,
    columnSizing,
  };

  if (enableColumnPinning) {
    (state as Record<string, unknown>).columnPinning = columnPinning;
  }
  if (enableColumnOrdering) {
    (state as Record<string, unknown>).columnOrder = columnOrder;
  }
  if (enableRowPinning) {
    (state as Record<string, unknown>).rowPinning = rowPinning;
  }

  return state;
};

export const createTableHandlers = (
  onSortingChange: (sorting: SortingState) => void,
  setColumnFilters: React.Dispatch<React.SetStateAction<ColumnFiltersState>>,
  setColumnVisibility: React.Dispatch<React.SetStateAction<ColumnVisibilityState>>,
  setColumnSizing: React.Dispatch<React.SetStateAction<ColumnSizingState>>,
  enableColumnPinning: boolean,
  onColumnPinningChange:
    | ((
      updater:
        | ColumnPinningState
        | ((old: ColumnPinningState) => ColumnPinningState),
    ) => void)
    | undefined,
  setColumnPinning: React.Dispatch<React.SetStateAction<ColumnPinningState>>,
  enableColumnOrdering: boolean,
  onColumnOrderChange:
    | ((
      updater:
        | ColumnOrderState
        | ((old: ColumnOrderState) => ColumnOrderState),
    ) => void)
    | undefined,
  setColumnOrder: React.Dispatch<React.SetStateAction<ColumnOrderState>>,
  enableRowPinning: boolean,
  onRowPinningChange:
    | ((
      updater: RowPinningState | ((old: RowPinningState) => RowPinningState),
    ) => void)
    | undefined,
  setRowPinning: React.Dispatch<React.SetStateAction<RowPinningState>>,
) => {
  const handlers = {
    onSortingChange,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnSizingChange: setColumnSizing,
  };

  if (enableColumnPinning) {
    (handlers as Record<string, unknown>).onColumnPinningChange = (
      updater:
        | ColumnPinningState
        | ((old: ColumnPinningState) => ColumnPinningState),
    ) => {
      // Always call the localStorage storage handler first
      setColumnPinning(updater);
      // Then call the prop handler if provided
      if (onColumnPinningChange) {
        onColumnPinningChange(updater);
      }
    };
  }
  if (enableColumnOrdering) {
    (handlers as Record<string, unknown>).onColumnOrderChange = (
      updater: ColumnOrderState | ((old: ColumnOrderState) => ColumnOrderState),
    ) => {
      // Always call the localStorage storage handler first
      setColumnOrder(updater);
      // Then call the prop handler if provided
      if (onColumnOrderChange) {
        onColumnOrderChange(updater);
      }
    };
  }
  if (enableRowPinning) {
    (handlers as Record<string, unknown>).onRowPinningChange = (
      updater: RowPinningState | ((old: RowPinningState) => RowPinningState),
    ) => {
      // Always call the localStorage storage handler first
      setRowPinning(updater);
      // Then call the prop handler if provided
      if (onRowPinningChange) {
        onRowPinningChange(updater);
      }
    };
  }

  return handlers;
};

/**
 * Ce dont dépend le rendu des cellules d'une ligne, hors donnée de la ligne :
 * définitions de colonnes et état des colonnes (visibilité, ordre, épinglage,
 * tailles pour les décalages des colonnes épinglées).
 */
export const getRowRenderVersion = <T extends RowData>(table: AppTable<T>): readonly unknown[] => {
  const state = table.store.state;
  return [
    table.options.columns, state.columnVisibility, state.columnOrder, state.columnPinning,
    state.columnSizing, state.rowPinning,
  ];
};
